import { formatClock } from '../format.ts';
import type { AuditEventView } from '../types.ts';

interface AuditPanelProps {
  readonly events: readonly AuditEventView[];
  readonly onRefresh: () => void;
  readonly busy: boolean;
}

/**
 * The caller's own audit trail, newest first.
 *
 * Shown in the UI because the trail is a deliverable, not a by-product: an auditor's
 * question is "what does the record say happened", and answering it from a screen during
 * the demo is more convincing than promising a DynamoDB table exists. The server scopes
 * the query to the signed-in subject, so this panel cannot become a way to read someone
 * else's history.
 *
 * Every field rendered here is one the audit contract permits. There is nothing to
 * filter out, which is the point — the record was designed so that it could be shown.
 */
export function AuditPanel({ events, onRefresh, busy }: AuditPanelProps) {
  return (
    <section className="readout" aria-labelledby="audit-heading">
      <div className="notice__head" style={{ marginBottom: '0.5rem' }}>
        <p className="eyebrow" id="audit-heading" style={{ marginBottom: 0 }}>
          Audit trail
        </p>
        <button type="button" className="button--quiet" onClick={onRefresh} disabled={busy}>
          Refresh
        </button>
      </div>

      {events.length === 0 ? (
        <p className="muted">
          Nothing recorded yet. Confirm a booking and the events appear here as they are written.
        </p>
      ) : (
        <ol className="trail">
          {events.map((event) => (
            <li key={event.eventId} data-outcome={event.outcome}>
              <span className="trail__time">{formatClock(event.occurredAt)}</span>
              <span className="trail__type">{event.eventType}</span>
              {event.reason ? <span className="trail__reason">{event.reason}</span> : null}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
